import { useState, type ReactNode } from 'react';
import { Building2, Globe, Info, Instagram, MessageSquare, Save } from 'lucide-react';
import { Button, SearchableSelect, SelectField, SettingsSection, Switch, type SearchableOption } from '@/design-system';
import { useCompaniesListQuery } from '@/features/companies';
import type { ChannelAvailability } from '../../api/meta-channel-availability.service';
import { useMetaAvailability, useMetaOverride } from '../../hooks/useMetaAvailability';

const CHANNELS = [
  { key: 'whatsappCloud', label: 'WhatsApp Cloud (Meta Oficial)', icon: MessageSquare },
  { key: 'messenger', label: 'Facebook Messenger', icon: MessageSquare },
  { key: 'instagram', label: 'Instagram Direct', icon: Instagram },
] as const;
type OverridePatch = Partial<Record<keyof ChannelAvailability, boolean | null>>;

function CompanyOverride({ companyId, companySelector }: { companyId: string; companySelector: (disabled: boolean) => ReactNode }) {
  const { query, save } = useMetaOverride(companyId);
  const [draft, setDraft] = useState<OverridePatch>({});
  const busy = save.isPending;
  const ready = !!companyId && !!query.data && !query.isFetching && !query.isError;
  return <div className="space-y-4">
    {save.isError && <p role="alert" className="text-sm text-red-700">Não foi possível salvar a configuração da empresa.</p>}
    {save.isSuccess && <p role="status" className="text-sm text-emerald-700">Configuração da empresa salva.</p>}
    <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-4">
      {companySelector(busy)}
      {CHANNELS.map((channel) => {
        const value = channel.key in draft ? draft[channel.key] : query.data?.[channel.key];
        return <SelectField key={channel.key} label={channel.key === 'whatsappCloud' ? 'WhatsApp Cloud' : channel.label} disabled={!ready || busy} value={value == null ? 'global' : value ? 'on' : 'off'}
          onChange={(next) => { setDraft((previous) => ({ ...previous, [channel.key]: next === 'global' ? null : next === 'on' })); save.reset(); }}
          options={[{ value: 'global', label: 'Usar configuração global' }, { value: 'on', label: 'Liberar' }, { value: 'off', label: 'Bloquear' }]} />;
      })}
    </div>
    {companyId && query.isFetching && <p role="status" className="text-sm text-gray-500">Carregando configuração da empresa…</p>}
    {companyId && query.isError && <div role="alert" className="text-sm text-red-700">Não foi possível carregar a configuração.
      <Button variant="subtle" onClick={() => void query.refetch()}>Tentar novamente</Button></div>}
    {companyId && <div className="flex justify-end gap-3">
      <Button isLoading={save.isPending} disabled={busy || !ready || !Object.keys(draft).length}
        onClick={() => save.mutate(draft, { onSuccess: () => setDraft({}) })}><Save size={16} />Salvar exceção</Button>
    </div>}
  </div>;
}

export function MetaChannelAvailabilitySection() {
  const { query, save } = useMetaAvailability();
  const [draft, setDraft] = useState<Partial<ChannelAvailability>>({});
  const [search, setSearch] = useState('');
  const [company, setCompany] = useState<SearchableOption | null>(null);
  const companyId = company?.value ?? '';
  const companies = useCompaniesListQuery({ page: 1, limit: 20, search, includeDeleted: false, includePurged: false });
  const options = (companies.data?.data ?? []).map((company) => ({ value: company.id, label: company.name }));
  const data = query.data ? { ...query.data, ...draft } : null;
  return <div className="space-y-6">
    <SettingsSection title="Disponibilidade global de canais" icon={Globe}
      description="Liga ou desliga os canais Meta para todas as empresas, respeitando as exceções individuais.">
      <div className="grid gap-4 pt-4 lg:grid-cols-3">
        {query.isPending && <p role="status" className="py-4 text-sm text-gray-500">Carregando disponibilidade…</p>}
        {!query.isPending && !data && <div role="alert" className="py-4 text-sm text-red-700">Não foi possível carregar a disponibilidade.
          <Button variant="subtle" onClick={() => void query.refetch()}>Tentar novamente</Button></div>}
        {data && CHANNELS.map(({ key, label, icon: Icon }) => <div key={key} className="min-w-0 rounded-lg border border-gray-200 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-2"><Icon aria-hidden size={20} className="shrink-0 text-primary" />
              <p className="text-sm font-medium text-gray-900">{label}</p></div>
          <Switch hideLabel label={`Disponível: ${label}`} checked={data[key]} disabled={save.isPending}
            onChange={(value) => { setDraft((previous) => ({ ...previous, [key]: value })); save.reset(); }} />
          </div>
          <p className="mt-2 text-xs text-gray-500">{data[key] ? 'Disponível para todas as empresas (exceto exceção bloqueada)' : 'Bloqueado globalmente (exceto exceção liberada)'}</p>
        </div>)}
      </div>
      {save.isError && <p role="alert" className="text-sm text-red-700">Não foi possível salvar a disponibilidade global.</p>}
      {save.isSuccess && <p role="status" className="text-sm text-emerald-700">Disponibilidade global salva.</p>}
      <div className="mt-4 flex justify-end"><Button isLoading={save.isPending} disabled={!data || !Object.keys(draft).length}
        onClick={() => save.mutate(draft, { onSuccess: () => setDraft({}) })}><Save size={16} />Salvar disponibilidade global</Button></div>
    </SettingsSection>
    <SettingsSection title="Exceção por empresa" icon={Building2}
      description="Quando uma empresa específica precisa de comportamento diferente do global. Busque pelo nome para selecionar.">
      <div className="space-y-4 pt-5">
        <CompanyOverride key={companyId} companyId={companyId} companySelector={(disabled) => <SearchableSelect
          label="Empresa" value={companyId} selectedLabel={company?.label}
          options={options} search={search} onSearchChange={setSearch} onChange={setCompany}
          disabled={disabled} loading={companies.isFetching}
          error={companies.isError ? 'Não foi possível buscar empresas.' : undefined}
          placeholder="Busque ou selecione uma empresa"
        />} />
        <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
          <Info size={16} className="mt-0.5 shrink-0" /><p>Cada canal pode estar em 3 estados: <strong>Usar configuração global</strong> (segue o padrão), <strong>Liberar</strong> (habilita mesmo se global desligado) ou <strong>Bloquear</strong> (desliga mesmo se global ligado).</p>
        </div>
      </div>
    </SettingsSection>
  </div>;
}
